import { useCallback, useContext, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from '@/context/AuthContext';
import FiltersContext from '@/context/FiltersContext/FiltersContext';
import { useLocalFiltersRegistry } from '@/context/LocalFiltersRegistry/LocalFiltersRegistry';
import { trackSharedViewVerifyFailed } from '@/hooks/AdobeAnalyticsHooks/sharedViewAnalytics';
import { useReconstructOptionPaths } from '@/hooks/useReconstructOptionPaths';
import { useToast } from '@/storybook/components/Toast/Toast';
import { readCachedCountryCodes } from '@/models/filter/configs/general/countryFilter';
import { copyShareViewLink, publishSharedView } from '@/utils/filter/shareView';
import { verifyPublishedSharedView } from '@/utils/filter/verifyPublishedSharedView';

interface UseShareViewResult {
  handleShareView: () => void;
  isLoading: boolean;
}

const useShareView = (): UseShareViewResult => {
  const { t } = useTranslation();
  const { filterState, optionPathsByFilter } = useContext(FiltersContext);
  const { userInfo } = useAuthContext();
  const { getAllLocalFilters } = useLocalFiltersRegistry();
  const { makeToast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const sharingRef = useRef(false);
  const reconstructOptionPaths = useReconstructOptionPaths();

  const handleShareView = useCallback(async () => {
    if (sharingRef.current) return;
    sharingRef.current = true;
    setIsLoading(true);
    try {
      const resolvedOptionPaths = await reconstructOptionPaths(filterState, optionPathsByFilter);
      const sharedFilterId = await publishSharedView({
        globalFilters: filterState,
        localFilters: getAllLocalFilters(),
        resolvedCountryCodes: readCachedCountryCodes(),
        optionPaths: resolvedOptionPaths,
        senderCountryCodes: userInfo?.countries ?? [],
      });

      if (!(await verifyPublishedSharedView(sharedFilterId))) {
        trackSharedViewVerifyFailed('shareButton');
        makeToast({
          content: String(t('global.filters.shareViewVerifyError')),
          appearance: 'error',
        });
        return;
      }

      await copyShareViewLink(sharedFilterId);
      makeToast({
        content: String(t('global.filters.shareViewSuccess')),
        appearance: 'success',
        autoClose: 5000,
      });
    } catch {
      makeToast({
        content: String(t('global.filters.shareViewError')),
        appearance: 'error',
      });
    } finally {
      sharingRef.current = false;
      setIsLoading(false);
    }
  }, [filterState, optionPathsByFilter, reconstructOptionPaths, userInfo, getAllLocalFilters, makeToast, t]);

  return { handleShareView, isLoading };
};

export default useShareView;
